import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { websocketService } from '../services/websocket';
import { notificationsService } from '../services/notifications';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { tokens, user } = useAuth();
  const qc = useQueryClient();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [teamActivities, setTeamActivities] = useState([]);
  const [planUnlocked, setPlanUnlocked] = useState(null); // {team_id, team_name, fee_type}
  const [isLoading, setIsLoading] = useState(false);
  const [recordingUpdates, setRecordingUpdates] = useState(null); // Latest recording update

  // Fetch notifications from API when user is authenticated
  const fetchNotifications = useCallback(async () => {
    if (!tokens?.access) return;

    setIsLoading(true);
    try {
      const data = await notificationsService.getNotifications({ limit: 50 });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      logger.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [tokens?.access]);

  // Fetch notifications on mount when authenticated
  useEffect(() => {
    if (tokens?.access && user) {
      fetchNotifications();
    }
  }, [tokens?.access, user, fetchNotifications]);

  // Connect to WebSocket when user is authenticated
  useEffect(() => {
    if (tokens?.access && user) {
      websocketService.connect(tokens.access);
    } else {
      websocketService.disconnect();
    }

    return () => {
      websocketService.disconnect();
    };
  }, [tokens?.access, user]);

  // Set up WebSocket event listeners
  useEffect(() => {
    const handleConnected = () => {
      logger.log('✅ NotificationContext: WebSocket connected');
      setIsConnected(true);
    };

    const handleDisconnected = () => {
      logger.log('❌ NotificationContext: WebSocket disconnected');
      setIsConnected(false);
    };

    const handleNotification = (data) => {
      logger.log('🔔 Real-time notification received:', data);
      // Add new notification, avoiding duplicates
      setNotifications(prev => {
        const exists = prev.some(n => n.id === data.notification.id);
        if (exists) {
          logger.log('⚠️ Duplicate notification, skipping:', data.notification.id);
          return prev;
        }
        logger.log('✅ Adding new notification to list:', data.notification);
        return [data.notification, ...prev];
      });
      setUnreadCount(prev => prev + 1);

      // Show toast for team invitation
      if (data.notification.notification_type === 'team_invitation') {
        toast.info(data.notification.title);
      }
    };

    const handleToast = (data) => {
      const { message, level } = data;
      switch (level) {
        case 'success':
          toast.success(message);
          break;
        case 'error':
          toast.error(message);
          break;
        case 'warning':
          toast.warning(message);
          break;
        default:
          toast.info(message);
      }
    };

    const handleBadgeUpdate = (data) => {
      setUnreadCount(data.unread_count);
    };

    const handleTeamActivity = (data) => {
      setTeamActivities(prev => [data.activity, ...prev.slice(0, 49)]);
    };

    const handleQuestionCompleted = (data) => {
      toast.info(`${data.user_name} completed a question!`);
      setTeamActivities(prev => [{
        type: 'question_completed',
        user_name: data.user_name,
        question_id: data.question_id,
        team_id: data.team_id,
        timestamp: new Date().toISOString(),
      }, ...prev.slice(0, 49)]);
    };

    const handleAffirmationReceived = (data) => {
      toast.success(`${data.from_user_name} affirmed your choices!`);
    };

    const handleChatMessage = (data) => {
      // Only show toast if not viewing that channel
      // (This logic can be expanded based on current view state)
      setTeamActivities(prev => [{
        type: 'chat_message',
        message: data.message,
        team_id: data.team_id,
        channel_id: data.channel_id,
        timestamp: new Date().toISOString(),
      }, ...prev.slice(0, 49)]);
    };

    const handleRecordingUpdate = (data) => {
      // Set the latest recording update - components can watch this to update their UI
      // Add timestamp to ensure React detects each update as unique
      setRecordingUpdates({ ...data.update, _timestamp: Date.now() });
    };

    const handleNewPost = (data) => {
      setTeamActivities(prev => [{
        type: 'new_post',
        post_type: data.post_type,
        post: data.post,
        team_id: data.team_id,
        timestamp: new Date().toISOString(),
      }, ...prev.slice(0, 49)]);
    };

    const handlePlanUnlocked = (data) => {
      // Care plan / subscription settled for one of my teams — the global
      // celebration modal watches this.
      setPlanUnlocked({
        team_id: data.team_id,
        team_name: data.team_name,
        fee_type: data.fee_type,
        // Who gave (non-anonymous sponsorships only) — lets the celebration
        // modal offer the "Sponsored by" display in the moment.
        sponsor_display: data.sponsor_display || null,
        sponsorship_id: data.sponsorship_id || null,
        _timestamp: Date.now(),
      });
      // Every surface that reads paid flags (About tab, billing, locked
      // cards) refreshes without a manual reload.
      if (data?.team_id) {
        qc.invalidateQueries({ queryKey: ['team', data.team_id] });
        qc.invalidateQueries({ queryKey: ['team-billing', data.team_id] });
      }
      // Q10-30 previews (is_locked) open for answering straight away.
      qc.invalidateQueries({ queryKey: ['questions-list'] });
    };

    // Vote activity (open / nomination / ballot / close / cancel) — refresh
    // the polls wherever they render (Votes tab, Posts-feed embed).
    const handleElectionUpdate = (data) => {
      if (!data?.team_id) return;
      qc.invalidateQueries({ queryKey: ['team-elections', data.team_id] });
      if (data.status && data.status !== 'open') {
        // A close can seat an office — refresh members + formation too.
        qc.invalidateQueries({ queryKey: ['team-members', data.team_id] });
        qc.invalidateQueries({ queryKey: ['formation-status', data.team_id] });
      }
    };

    const handlePulseCheckin = (data) => {
      setTeamActivities(prev => [{
        type: 'weekly_pulse_checkin',
        checkin: data.checkin,
        pulse_id: data.pulse_id,
        team_id: data.team_id,
        timestamp: new Date().toISOString(),
      }, ...prev.slice(0, 49)]);
    };

    // Advocacy (workbook) events — backend signals fan out to user_{id}
    // when Exercise / Artifact / ActionPlan rows change. We invalidate the
    // matching React Query caches so every open tab updates without
    // polling.
    //
    // Invalidations are scoped tightly: the broad `['advocacy','exercises']`
    // and `['advocacy','artifacts']` list caches only refresh on transitions
    // that change the list shape (new row, status flip). Routine field-edit
    // saves only touch the single-exercise cache. This stops every keystroke
    // from snowballing into list-wide refetches across every mounted view.
    const handleAdvocacyExerciseUpdated = (data) => {
      if (data?.exercise_id) {
        qc.invalidateQueries({
          queryKey: ['advocacy', 'exercise', data.exercise_id],
        });
      }
      // Only refresh the list when the list-visible state actually changes.
      if (data?.created || data?.status === 'COMPLETED') {
        qc.invalidateQueries({ queryKey: ['advocacy', 'exercises'] });
      }
    };
    const handleAdvocacyArtifactUpdated = (data) => {
      if (data?.exercise_id) {
        qc.invalidateQueries({
          queryKey: ['advocacy', 'exercise', data.exercise_id],
        });
      }
      if (data?.created) {
        qc.invalidateQueries({ queryKey: ['advocacy', 'artifacts'] });
      }
    };
    const handleAdvocacyActionPlanUpdated = (data) => {
      if (data?.exercise_id) {
        qc.invalidateQueries({
          queryKey: ['advocacy', 'action-plan', data.exercise_id],
        });
        qc.invalidateQueries({
          queryKey: ['advocacy', 'exercise', data.exercise_id],
        });
      }
      // Create / delete change the list shape; routine edits don't.
      if (data?.created || data?.deleted) {
        qc.invalidateQueries({ queryKey: ['advocacy', 'action-plan'] });
        qc.invalidateQueries({ queryKey: ['advocacy', 'exercises'] });
        qc.invalidateQueries({ queryKey: ['advocacy', 'artifacts'] });
      }
    };
    // Real-time fan-out for collaborative-exercise contributions. Backend
    // broadcasts to team_{team_id} on every ExerciseContribution save, so
    // every active team member's open tab refreshes without polling:
    //   • status feed → "Team contributions" panel + post-submit footer
    //   • exercise + artifact → live merge view (renders the new voice)
    const handleAdvocacyContributionUpdated = (data) => {
      if (!data?.exercise_id) return;
      qc.invalidateQueries({
        queryKey: ['advocacy', 'contributions', data.exercise_id],
      });
      qc.invalidateQueries({
        queryKey: ['advocacy', 'contributions', data.exercise_id, 'status'],
      });
      qc.invalidateQueries({
        queryKey: ['advocacy', 'exercise', data.exercise_id],
      });
      qc.invalidateQueries({ queryKey: ['advocacy', 'artifacts'] });
    };

    // Subscribe to events
    websocketService.on('connected', handleConnected);
    websocketService.on('disconnected', handleDisconnected);
    websocketService.on('notification', handleNotification);
    websocketService.on('toast', handleToast);
    websocketService.on('badge_update', handleBadgeUpdate);
    websocketService.on('team_activity', handleTeamActivity);
    websocketService.on('question_completed', handleQuestionCompleted);
    websocketService.on('affirmation_received', handleAffirmationReceived);
    websocketService.on('chat_message', handleChatMessage);
    websocketService.on('recording_update', handleRecordingUpdate);
    websocketService.on('team_new_post', handleNewPost);
    websocketService.on('weekly_pulse_checkin', handlePulseCheckin);
    websocketService.on('team_plan_unlocked', handlePlanUnlocked);
    websocketService.on('team_election_update', handleElectionUpdate);
    websocketService.on('advocacy_exercise_updated', handleAdvocacyExerciseUpdated);
    websocketService.on('advocacy_artifact_updated', handleAdvocacyArtifactUpdated);
    websocketService.on('advocacy_action_plan_updated', handleAdvocacyActionPlanUpdated);
    websocketService.on('advocacy_contribution_updated', handleAdvocacyContributionUpdated);

    // Cleanup
    return () => {
      websocketService.off('connected', handleConnected);
      websocketService.off('disconnected', handleDisconnected);
      websocketService.off('notification', handleNotification);
      websocketService.off('toast', handleToast);
      websocketService.off('badge_update', handleBadgeUpdate);
      websocketService.off('team_activity', handleTeamActivity);
      websocketService.off('question_completed', handleQuestionCompleted);
      websocketService.off('affirmation_received', handleAffirmationReceived);
      websocketService.off('chat_message', handleChatMessage);
      websocketService.off('recording_update', handleRecordingUpdate);
      websocketService.off('team_new_post', handleNewPost);
      websocketService.off('weekly_pulse_checkin', handlePulseCheckin);
      websocketService.off('team_plan_unlocked', handlePlanUnlocked);
      websocketService.off('team_election_update', handleElectionUpdate);
      websocketService.off('advocacy_exercise_updated', handleAdvocacyExerciseUpdated);
      websocketService.off('advocacy_artifact_updated', handleAdvocacyArtifactUpdated);
      websocketService.off('advocacy_action_plan_updated', handleAdvocacyActionPlanUpdated);
      websocketService.off('advocacy_contribution_updated', handleAdvocacyContributionUpdated);
    };
  }, [qc]);

  const markAsRead = useCallback(async (notificationId) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    // Sync with backend
    try {
      await notificationsService.markAsRead(notificationId);
    } catch (error) {
      logger.error('Failed to mark notification as read:', error);
      // Revert on error
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: false } : n)
      );
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const previousNotifications = notifications;
    const previousCount = unreadCount;

    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);

    // Sync with backend
    try {
      await notificationsService.markAllAsRead();
    } catch (error) {
      logger.error('Failed to mark all notifications as read:', error);
      // Revert on error
      setNotifications(previousNotifications);
      setUnreadCount(previousCount);
    }
  }, [notifications, unreadCount]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      isConnected,
      isLoading,
      teamActivities,
      recordingUpdates,
      planUnlocked,
      clearPlanUnlocked: () => setPlanUnlocked(null),
      markAsRead,
      markAllAsRead,
      clearNotifications,
      refetchNotifications: fetchNotifications,
    }),
    [
      notifications,
      unreadCount,
      isConnected,
      isLoading,
      teamActivities,
      recordingUpdates,
      planUnlocked,
      markAsRead,
      markAllAsRead,
      clearNotifications,
      fetchNotifications,
    ],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
